import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Search } from 'lucide-react';
import { useAllAdminOrganizations } from '@/hooks/useAllAdminOrganizations';
import { formatDate } from '@/lib/format';

const PAGE_SIZE = 25;

/** Maps an "all" | "yes" | "no" select value to the optional boolean filter. */
function toBoolFilter(value: string): boolean | undefined {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return undefined;
}

/**
 * Admin Organizations list page. Materializes the flag-filtered organization
 * set client-side (see useAllAdminOrganizations) and performs name search,
 * alphabetical sort, and paging in the browser.
 */
export default function AdminOrganizationsPage() {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [nonProfitFilter, setNonProfitFilter] = useState('all');
  const [page, setPage] = useState(0);

  const filters = useMemo(
    () => ({ isPartner: toBoolFilter(partnerFilter), isNonProfit: toBoolFilter(nonProfitFilter) }),
    [partnerFilter, nonProfitFilter]
  );

  const { data: organizations, isLoading, error } = useAllAdminOrganizations(filters);

  // Resolve a partner id to its organization name from the in-memory set.
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    (organizations ?? []).forEach((org) => map.set(org.id, org.name));
    return map;
  }, [organizations]);

  // Case-insensitive name search + alphabetical sort, all client-side.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (organizations ?? [])
      .filter((org) => !term || org.name.toLowerCase().includes(term))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [organizations, search]);

  // Reset to the first page whenever the result set changes.
  useEffect(() => {
    setPage(0);
  }, [search, partnerFilter, nonProfitFilter, organizations]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageItems = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Organizations</h1>
        <p className="text-muted-foreground">
          Browse organizations and manage their pricing-tier flags and partner associations
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <Select value={partnerFilter} onValueChange={setPartnerFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Partner" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Partner: All</SelectItem>
            <SelectItem value="yes">Partners only</SelectItem>
            <SelectItem value="no">Non-partners</SelectItem>
          </SelectContent>
        </Select>
        <Select value={nonProfitFilter} onValueChange={setNonProfitFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Non-profit" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Non-profit: All</SelectItem>
            <SelectItem value="yes">Non-profits only</SelectItem>
            <SelectItem value="no">Non non-profits</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {error ? (
        <div className="text-center text-destructive py-8">
          <p>Failed to load organizations: {(error as Error)?.message || 'Unknown error'}</p>
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Non-profit</TableHead>
                <TableHead>Partner</TableHead>
                <TableHead>Source partner</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(8)].map((_, index) => (
                  <TableRow key={index}>
                    {[...Array(5)].map((__, cell) => (
                      <TableCell key={cell}>
                        <Skeleton className="h-4 w-[120px]" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : pageItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No organizations match the current filters.
                  </TableCell>
                </TableRow>
              ) : (
                pageItems.map((org) => (
                  <TableRow
                    key={org.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/dashboard/admin/organizations/${org.id}`)}
                  >
                    <TableCell className="font-medium">{org.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(org.createdUtc)}
                    </TableCell>
                    <TableCell>
                      {org.isNonProfit ? (
                        <Badge variant="default">Yes</Badge>
                      ) : (
                        <Badge variant="outline">No</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {org.isPartner ? (
                        <Badge variant="default">Yes</Badge>
                      ) : (
                        <Badge variant="outline">No</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {org.sourcePartnerId ? (
                        <Button
                          variant="link"
                          className="h-auto p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/admin/organizations/${org.sourcePartnerId}`);
                          }}
                        >
                          {nameById.get(org.sourcePartnerId) ?? org.sourcePartnerId}
                        </Button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {!isLoading && filtered.length > 0 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {safePage * PAGE_SIZE + 1}–
                {Math.min(safePage * PAGE_SIZE + PAGE_SIZE, filtered.length)} of {filtered.length}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={safePage <= 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {safePage + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={safePage >= totalPages - 1}
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
